import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Pencil, Wallet, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { money } from "@/lib/constants";
import WeeklyDaysPicker, { weeklyLabel } from "@/components/WeeklyDaysPicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const empty = {
  name: "", employee_id: "", mobile: "", email: "", designation: "", department: "",
  branch_id: "", joining_date: "", exit_date: "", monthly_salary: 0, reporting_time: "10:00",
  grace_minutes: 15, cutoff_time: "11:00", weekly_leave: "sunday", status: "active",
};

export default function Employees() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [salaryFor, setSalaryFor] = useState(null);
  const [salaryForm, setSalaryForm] = useState({ new_salary: 0, effective_from: "", reason: "" });

  const { data: employees } = useQuery({ queryKey: ["employees"], queryFn: async () => (await api.get("/employees")).data });
  const { data: branches } = useQuery({ queryKey: ["branches"], queryFn: async () => (await api.get("/branches")).data });
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const branchName = (id) => branches?.find((b) => b.id === id)?.name || "-";

  const openNew = () => {
    setEditing(null);
    setForm({
      ...empty,
      reporting_time: settings?.default_reporting_time || "10:00",
      grace_minutes: settings?.default_grace_minutes ?? 15,
      cutoff_time: settings?.default_cutoff_time || "11:00",
      weekly_leave: settings?.default_weekly_leave || "sunday",
    });
    setOpen(true);
  };
  const openEdit = (e) => { setEditing(e); setForm({ ...empty, ...e, exit_date: e.exit_date || "" }); setOpen(true); };

  const save = async () => {
    try {
      if (!editing && !form.branch_id) { toast.error("Please select a branch — the Employee ID is generated from its code"); return; }
      const payload = { ...form, monthly_salary: Number(form.monthly_salary), grace_minutes: Number(form.grace_minutes) };
      if (!payload.exit_date) payload.exit_date = null;
      if (editing) {
        await api.put(`/employees/${editing.id}`, payload);
        toast.success("Employee updated");
      } else {
        await api.post("/employees", payload);
        toast.success("Employee created");
      }
      setOpen(false);
      qc.invalidateQueries({ queryKey: ["employees"] });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this demo employee?")) return;
    try { await api.delete(`/employees/${id}`); toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["employees"] }); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveSalary = async () => {
    try {
      await api.post(`/employees/${salaryFor.id}/salary`, { ...salaryForm, new_salary: Number(salaryForm.new_salary) });
      toast.success("Salary updated");
      setSalaryFor(null);
      qc.invalidateQueries({ queryKey: ["employees"] });
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const field = (label, k, type = "text") => (
    <div>
      <label className="text-xs text-slate-500">{label}</label>
      <Input data-testid={`emp-${k}-input`} type={type} value={form[k] ?? ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })} />
    </div>
  );

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl sm:text-3xl font-head font-extrabold text-slate-900">Employees</h1>
        <Button data-testid="add-employee-button" onClick={openNew} className="bg-blue-600 hover:bg-blue-700"><Plus className="h-4 w-4 mr-1" /> Add</Button>
      </div>

      <div className="rounded-xl bg-blue-50 border border-blue-100 px-4 py-2.5 text-xs text-blue-700">
        Staff sign in with their Google account. Make sure each employee's <b>Email</b> matches the Google address they'll use to log in.
      </div>

      {/* Mobile card list */}
      <div className="lg:hidden space-y-3">
        {(employees || []).length === 0 && <div className="bg-white rounded-2xl border border-slate-200 p-6 text-sm text-slate-400">No employees yet. Tap Add to create one.</div>}
        {(employees || []).map((e) => (
          <div key={e.id} data-testid={`employee-card-${e.employee_id}`} className="bg-white rounded-2xl border border-slate-200 p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-semibold text-slate-900 truncate">{e.name}</div>
                <div className="text-xs text-slate-400">{e.designation || "—"}</div>
                <div className="text-xs font-mono text-slate-500 mt-0.5">{e.employee_id}</div>
              </div>
              <span className={`shrink-0 text-xs px-2 py-1 rounded-full ${e.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{e.status}</span>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
              <div><div className="text-slate-400">Branch</div><div className="text-slate-700">{branchName(e.branch_id)}</div></div>
              <div><div className="text-slate-400">Salary</div><div className="text-slate-700">{money(e.monthly_salary)}</div></div>
              <div className="col-span-2"><div className="text-slate-400">Email (Google login)</div><div className="text-slate-700 truncate">{e.email || "— not set —"}</div></div>
              <div className="col-span-2"><div className="text-slate-400">Timing</div><div className="text-slate-700">{e.reporting_time} +{e.grace_minutes}m / {e.cutoff_time} · Off: {weeklyLabel(e.weekly_leave)}</div></div>
            </div>
            <div className="mt-3 flex items-center gap-2 border-t border-slate-100 pt-3">
              <button data-testid={`edit-employee-m-${e.employee_id}`} onClick={() => openEdit(e)} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-slate-50 text-slate-600 text-sm active:bg-slate-100"><Pencil className="h-4 w-4" /> Edit</button>
              <button onClick={() => { setSalaryFor(e); setSalaryForm({ new_salary: e.monthly_salary, effective_from: "", reason: "" }); }} className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg bg-slate-50 text-slate-600 text-sm active:bg-slate-100"><Wallet className="h-4 w-4" /> Salary</button>
              {e.is_demo && <button onClick={() => del(e.id)} className="p-2 rounded-lg bg-red-50 text-red-600"><Trash2 className="h-4 w-4" /></button>}
            </div>
          </div>
        ))}
      </div>

      <div className="hidden lg:block bg-white rounded-2xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">ID</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Email (Google login)</th>
              <th className="text-left px-4 py-3 hidden sm:table-cell">Salary</th>
              <th className="text-left px-4 py-3 hidden lg:table-cell">Timing</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(employees || []).map((e) => (
              <tr key={e.id} data-testid={`employee-row-${e.employee_id}`} className="hover:bg-slate-50">
                <td className="px-4 py-3 font-medium text-slate-900">{e.name}<div className="text-xs text-slate-400">{e.designation}</div></td>
                <td className="px-4 py-3 font-mono text-xs">{e.employee_id}</td>
                <td className="px-4 py-3 hidden md:table-cell text-xs">{e.email || <span className="text-amber-600">not set</span>}</td>
                <td className="px-4 py-3 hidden sm:table-cell">{money(e.monthly_salary)}</td>
                <td className="px-4 py-3 hidden lg:table-cell text-xs">{e.reporting_time} +{e.grace_minutes}m / {e.cutoff_time}<div className="text-slate-400">Off: {weeklyLabel(e.weekly_leave)}</div></td>
                <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded-full ${e.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{e.status}</span></td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1 justify-end">
                    <button data-testid={`edit-employee-${e.employee_id}`} onClick={() => openEdit(e)} className="p-2 text-slate-500 hover:text-blue-600"><Pencil className="h-4 w-4" /></button>
                    <button title="Change salary" onClick={() => { setSalaryFor(e); setSalaryForm({ new_salary: e.monthly_salary, effective_from: "", reason: "" }); }} className="p-2 text-slate-500 hover:text-emerald-600"><Wallet className="h-4 w-4" /></button>
                    {e.is_demo && <button title="Delete" onClick={() => del(e.id)} className="p-2 text-slate-500 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Edit Employee" : "New Employee"}</DialogTitle></DialogHeader>
          <div className="flex flex-col gap-3 sm:grid sm:grid-cols-2">
            {field("Name", "name")}
            {editing ? (
              <div><label className="text-xs text-slate-500">Employee ID (read-only)</label><Input data-testid="emp-employee_id-input" value={form.employee_id || ""} readOnly disabled /></div>
            ) : (
              <div><label className="text-xs text-slate-500">Employee ID</label><Input data-testid="emp-employee_id-input" value="Auto-generated from branch code" readOnly disabled /></div>
            )}
            {field("Mobile", "mobile")}
            <div>
              <label className="text-xs text-slate-500">Email (used for Google login)</label>
              <Input data-testid="emp-email-input" type="email" value={form.email ?? ""} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="name@gmail.com" />
            </div>
            {field("Designation", "designation")}
            {field("Department", "department")}
            <div>
              <label className="text-xs text-slate-500">Branch</label>
              <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                <SelectTrigger data-testid="emp-branch-select"><SelectValue placeholder="Branch" /></SelectTrigger>
                <SelectContent>{(branches || []).map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <label className="text-xs text-slate-500">Weekly Paid Leave (select one or more days)</label>
              <WeeklyDaysPicker value={form.weekly_leave} onChange={(v) => setForm({ ...form, weekly_leave: v })} />
            </div>
            {field("Joining Date", "joining_date", "date")}
            {field("Exit Date", "exit_date", "date")}
            {field("Monthly Salary", "monthly_salary", "number")}
            {field("Reporting Time", "reporting_time", "time")}
            {field("Grace (minutes)", "grace_minutes", "number")}
            {field("Cutoff Time", "cutoff_time", "time")}
            <div>
              <label className="text-xs text-slate-500">Status</label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="emp-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="exited">Exited</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button data-testid="save-employee-button" onClick={save} className="w-full bg-blue-600 hover:bg-blue-700 mt-2">Save</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!salaryFor} onOpenChange={(v) => !v && setSalaryFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Change Salary · {salaryFor?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-xs text-slate-500">New Monthly Salary</label><Input data-testid="new-salary-input" type="number" value={salaryForm.new_salary} onChange={(e) => setSalaryForm({ ...salaryForm, new_salary: e.target.value })} /></div>
            <div><label className="text-xs text-slate-500">Effective From</label><Input data-testid="salary-effective-input" type="date" value={salaryForm.effective_from} onChange={(e) => setSalaryForm({ ...salaryForm, effective_from: e.target.value })} /></div>
            <div><label className="text-xs text-slate-500">Reason</label><Input data-testid="salary-reason-input" value={salaryForm.reason} onChange={(e) => setSalaryForm({ ...salaryForm, reason: e.target.value })} /></div>
            <Button data-testid="save-salary-button" onClick={saveSalary} disabled={!salaryForm.effective_from || !salaryForm.reason} className="w-full bg-blue-600 hover:bg-blue-700">Update Salary</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
